import React, { useState } from 'react';
import {
  FormFieldLabel,
  Table,
  Tooltip,
  Select
} from '../../../ui';
import './style.css';
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectBookings, selectCustomer } from "../../../redux/slices/customer-slice";
import {
  BookingStatusEnum,
  ICustomerBooking,
} from "../../../models/customer-data.interface";
import MessageModal from '../../base/message-modal';
import { formatter } from '../../../utils/time-utils';
import { useSearchParams } from "react-router-dom";
import BookingErrorsModal from '../../base/booking-errors-modal';
import { BookingPrePaymentState, BookingStatus } from './helper';
import { cancelCustomerBooking, refundCustomerBooking } from "../../../redux/slices/customer-bookings";
import { selectNotificationStore, setErrorDialog } from '../../../redux/slices/notification-slice';
import TooltipInfoIcon from '../../base/tooltip-info-icon';
import { ImageIcon } from '../../base/image-icon/ImageIcon';
import BookingDetailsModal from '../../base/booking-details-modal';
import CopyActionWrapper from '../../base/copy-action-wrapper';
import BookingRefundsModal from "../../base/booking-refunds-modal";

function generateBookingStatusDescriptionInfoIcon() {
  return <TooltipInfoIcon testId="infoIconBookingStatusDescription">
    <b>Received</b>:Request for booking received bot not confirmed.<br />
    <b>Confirmed</b>:Received booking request successfully confirmed.<br />
    <b>Reserved</b>:Booking request successfully reserved.<br />
    <b>Postpaid canceled</b>:Booking canceled after it&apos;s paid.<br />
    <b>Prepaid canceled</b>:Booking canceled before it&apos;s paid.<br />
    <b>Rejected</b>:Booking reservation rejected.<br />
  </TooltipInfoIcon>;
}

function generatePaymentStatusDescriptionInfoIcon() {
  return <TooltipInfoIcon testId="infoIconPaymentStatusDescription">
    <b>Prepayment (Wait for 3DS)</b>:Awaiting for payment to be verified.<br />
    <b>Prepayment (In Progress)</b>:Payment verified with 3DS, but not captured.<br />
    <b>Prepayment (Paid)</b>:Payment successfully captured.<br />
    <b>Prepayment (Rejected)</b>:Payment capture rejected.<br />
    <b>Prepayment (Canceled)</b>:Payment authorization canceled.<br />
    <b>Prepayment (Refund Initiated)</b>:Payment refund initiated.<br />
    <b>Prepayment (Refunded)</b>:Payment refunded.<br />
    <b>Prepayment (Refunded rejected)</b>:Payment refund rejected.<br />
    <b>Payment (On Arrival)</b>:Payment paid or will be paid on arrival.<br />
  </TooltipInfoIcon>;
}

export default function BookingHistoryDataView() {
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const [searchParams] = useSearchParams();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errors, setErrors] = useState([]);
  const [action, setAction] = useState(null);

  const [showRefundsModal, setShowRefundsModal] = useState(false);
  const [refunds, setRefunds] = useState([]);

  const customer = useAppSelector(selectCustomer());
  const notification = useAppSelector(selectNotificationStore());
  const bookings = useAppSelector(selectBookings());
  const dispatch = useAppDispatch();

  const handleActionClose = () => {
    setAction(null);
    setSelectedBooking(null);
    setShowModal(false);
    setShowDetailsModal(false);
  }

  const handleOnModalShow = (booking: ICustomerBooking, act: string) => {
    if (action != act) {
      if (act === 'Show_Details') {
        setShowDetailsModal(true);
      } else {
        setShowModal(true);
      }

      setAction(act);
      setSelectedBooking(booking);
    }
  }

  const handleOnErrorClose = () => {
    setShowErrorModal(false);
    setErrors([]);
  }

  const handleOnErrorModalShow = (errors) => {
    setErrors(errors);
    setShowErrorModal(true);
  }

  const handleOnRefundsModalShow = (refunds) => {
    setRefunds(refunds);
    setShowRefundsModal(true);
  }

  const handleOnRefundsClose = () => {
    setShowRefundsModal(false);
    setRefunds([]);
  }

  const handleActionBooking = () => {
    handleActionClose();

    if (!customer.id || !selectedBooking?.id) {
      dispatch(setErrorDialog({
        code: 'BOOKING_ERROR_CODE',
        title: 'Unable to cancel booking',
        message: 'Customer booking cannot be canceled, some data is missing.'
      }));
      return
    }
    if (action === 'Cancel') {
      dispatch(cancelCustomerBooking({
        customerId: customer.id,
        bookingId: selectedBooking?.id,
        externalBookingId: searchParams.get('bookingId'),
        userInput: searchParams.get('userInput'),
      }))
    } else if (action === 'Refund') {
      dispatch(refundCustomerBooking({
        customerId: customer.id,
        bookingId: selectedBooking?.id
      }))
    }

  }

  function renderPaymentState(booking: ICustomerBooking) {
    if (!booking) return null;

    const { paymentDetails } = booking;

    const { refunds, errors } = paymentDetails;

    const state = BookingPrePaymentState[paymentDetails?.paymentState];
    const label = state ? `Prepayment (${state})` : 'Payment (On Arrival)'

    if (errors?.length > 0 || refunds?.length > 0) {
      return (<div>
        {label}
        { errors?.length > 0 ?
            <ImageIcon className='status-icon status-icon--error' icon="semantic-error" onClick={() => handleOnErrorModalShow(errors)}>Click to see the errors</ImageIcon>
           : ''}
        { refunds?.length > 0 ?
            <ImageIcon className='status-icon status-icon--info' icon="semantic-info" onClick={() => handleOnRefundsModalShow(refunds)}>Click to see the refunds</ImageIcon>
          : ''}
        </div>);
    }

    if (refunds?.length > 0) {
      return ;
    }

    return label;
  }

  function renderBookingStatus(booking: ICustomerBooking) {
    if (!booking) return null;

    const { order } = booking;

    const bookingStatus = booking?.status || order?.status;

    const statusText = BookingStatus[bookingStatus] || "-";

    if (booking?.updateDateTime) {
      const { updateDateTime } = booking;

      const dateString = updateDateTime ? `${formatter.format(new Date(updateDateTime))}` : '';
      return <Tooltip content={`${dateString}`}>{`${statusText}`}</Tooltip>;
    }

    return statusText;
  }

  function renderActionButtons(booking: ICustomerBooking) {
    const cancelableStatuses: BookingStatusEnum[] = ['RESERVED', 'REQUEST_CONFIRMED', 'REQUEST_RECEIVED'];
    const canceledStatuses: BookingStatusEnum[] = ['POSTPAID_CANCELED', 'PREPAID_CANCELLED'];

    const bookingState = booking?.status ? booking?.status : booking?.order?.status
    const isCancelable = booking?.offer?.startDate && new Date(booking.offer.startDate) > new Date()
      && cancelableStatuses.includes(bookingState)
    const isRefundable = canceledStatuses.includes(booking?.status) && booking.paymentDetails?.paymentState !== 'REFUNDED';

    return (
      <Select onChange={(e) => handleOnModalShow(booking, e.target.value)} className='action-combo' value={'Select'} testId={`action-booking-${booking?.id}-btn`}>
        <Select.Item value='Select' testId={`select-booking-${booking?.id}-btn`}>Select</Select.Item>
        <Select.Item disabled={!isCancelable} value='Cancel' testId={`cancel-booking-${booking?.id}-btn`}>Cancel</Select.Item>
        <Select.Item disabled={!isRefundable} value='Refund' testId={`refund-booking-${booking?.id}-btn`}>Refund</Select.Item>
        <Select.Item value='Show_Details' testId={`details-booking-${booking?.id}-btn`}>Show Details</Select.Item>
      </Select>
    );
  }

  function renderPickUpAndReturnDate(booking: ICustomerBooking) {
    const pickupDate = booking?.offer.startDate;
    const returnDate = booking?.offer.endDate;

    const formattedPickupDate = formatter.format(new Date(pickupDate));
    const formattedReturnDate = formatter.format(new Date(returnDate));

    return `${formattedPickupDate} - ${formattedReturnDate}`
  }

  function renderItem(booking: ICustomerBooking) {
    const emgBookingIdentifier = booking?.order?.externalIdentifiers?.find(identfier => identfier.type === 'EUROPCAR_ORDER_ID');
    const bookingAmount = booking?.offer?.pricings?.find(it => it.productCode === 'RESIDENCE_CURRENCY')?.elements.find(it => it.type === 'TOTAL')?.price
    const amount = booking?.paymentDetails?.totalAmount?.grossAmount ?
      booking?.paymentDetails?.totalAmount?.grossAmount + " " + booking?.paymentDetails?.totalAmount?.currency :
      bookingAmount?.grossAmount + " " + bookingAmount?.currency;

    const emgBookingId =
      emgBookingIdentifier?.id?.length > 16 ? emgBookingIdentifier?.id.substring(0, 16) + '...' : emgBookingIdentifier?.id;


    return (
      <Table.Tr key={booking?.id + '-' + booking?.status} className={emgBookingIdentifier?.id === searchParams.get('bookingId') ? 'table-row row-selected' : 'table-row'}>
        <Table.Td>
          {
            emgBookingId ? <CopyActionWrapper>{emgBookingId}</CopyActionWrapper> : '-'
          }
        </Table.Td>

        <Table.Td>{booking?.createDateTime ? formatter.format(new Date(booking?.createDateTime)) : ( booking?.order?.timestamp ? formatter.format(new Date(booking?.order?.timestamp)) : '-')}</Table.Td>
        <Table.Td>{renderPickUpAndReturnDate(booking)}</Table.Td>
        <Table.Td>{renderBookingStatus(booking)}</Table.Td>
        <Table.Td>{renderPaymentState(booking)}</Table.Td>
        <Table.Td>{amount}</Table.Td>
        <Table.Td>{renderActionButtons(booking)}</Table.Td>
      </Table.Tr >
    );
  }

  function renderNotFoundMessage(bookingList: ICustomerBooking[]) {
    let message = null;

    if ((!bookingList || bookingList?.length === 0) && bookings?.status !== 'failed') {
      message = 'No Bookings for customer.';

      if (bookings?.filter?.dateRange?.start) {
        message = 'No Bookings found for customer in given time frame.';
      } else if (bookings?.filter?.prePaymentState) {
        message = 'No Bookings found for customer in given prepayment state.';
      } else if (bookings?.filter?.bookingStatus) {
        message = 'No Bookings found for customer in booking state.';
      }
    }

    if (!message) return null;

    return <FormFieldLabel testId="noBookingsLabel">{message}</FormFieldLabel>
  }

  function renderBookingTable(bookingList: ICustomerBooking[], label: string) {
    return (
      <>
        {bookingList && bookingList?.length > 0 && (
          <Table colored={true} testId={'booking-history-table-' + label}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>EMG Booking ID</Table.Th>
                <Table.Th>Creation Date </Table.Th>
                <Table.Th>Pickup and Return Date</Table.Th>
                <Table.Th>Booking Status{generateBookingStatusDescriptionInfoIcon()}</Table.Th>
                <Table.Th>Payment Status{generatePaymentStatusDescriptionInfoIcon()}</Table.Th>
                <Table.Th>Gross Amount</Table.Th>
                <Table.Th>Details & Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{bookingList?.map((booking) => renderItem(booking))}</Table.Tbody>
          </Table>
        )}

        {renderNotFoundMessage(bookingList)}

        {bookings?.status === 'failed' && (
          <FormFieldLabel testId={'errorBookingsLabel-' + label}>{notification?.errorDialog?.message}</FormFieldLabel>)}
      </>
    );
  }

  return (
    <>
      <div className="customer-detail">
        Future Bookings
        {renderBookingTable(bookings?.futurePresentationData, 'future')}
      </div>
      <div className="customer-detail">
        Past Bookings
        {renderBookingTable(bookings?.pastPresentationData, 'past')}
      </div>

      <BookingErrorsModal errors={[...errors]} showModal={showErrorModal} handleOnClose={handleOnErrorClose} />

      <BookingRefundsModal refunds={[...refunds]} showModal={showRefundsModal} handleOnClose={handleOnRefundsClose} />

      <MessageModal
        message={action && `Are you sure you want to ${action} this booking?`}
        showModal={showModal}
        handleOnClose={handleActionClose}
        handleOnConfirm={handleActionBooking}
        action='Yes'
        title={`${action} booking`} />

      <BookingDetailsModal
        title='Booking Details'
        showModal={showDetailsModal}
        booking={selectedBooking}
        handleOnModalClose={handleActionClose}
      />
    </>
  );
}
